import moment from "moment-timezone";

export const liveAnalyticsMixin = {
  computed: {
    departments(this: any) {
      return this.$store.state.department?.departments || [];
    },
  },
  methods: {
    // Parse agent departments from JSON string or array
    parseAgentDepartments(agent: any) {
      let departments = agent.departments || [];
      if (typeof departments === "string") {
        try {
          departments = JSON.parse(departments);
        } catch {
          departments = [];
        }
      }
      return departments;
    },

    // Get lowercase department names from selected department IDs
    getSelectedDepartmentNames(this: any, selectedDepartments: string[]) {
      return selectedDepartments.map((deptId) => {
        const dept = this.departments.find((d: any) => d.id === deptId);
        return dept?.name?.toLowerCase() || deptId?.toLowerCase();
      });
    },

    // Get display-friendly department name from a raw dept value (ID or name string)
    getDepartmentLabel(this: any, rawDept: string) {
      if (!rawDept) return null;
      const deptById = this.departments.find((d: any) => d.id === rawDept);
      if (deptById) return deptById.name;
      const deptByName = this.departments.find(
        (d: any) => d.name?.toLowerCase() === rawDept?.toLowerCase()
      );
      if (deptByName) return deptByName.name;
      return null;
    },

    getRawDepartmentFromChat(chat: any) {
      return chat.stateVariables?.userQueryTopic || chat.department || null;
    },

    getChatDepartmentId(this: any, chat: any) {
      const rawDept = this.getRawDepartmentFromChat(chat);
      if (!rawDept) return null;

      const deptById = this.departments.find((d: any) => d.id === rawDept);

      if (deptById) return deptById.id;

      const deptByName = this.departments.find(
        (d: any) => d.name?.toLowerCase() === rawDept?.toLowerCase()
      );
      if (deptByName) return deptByName.id;

      return rawDept;
    },

    chatMatchesDepartmentFilter(this: any, chat: any, selectedDepartments: string[]) {
      if (!selectedDepartments?.length) return true;

      const chatDepartment = this.getRawDepartmentFromChat(chat);

      if (!chatDepartment) return false;

      const chatDeptLower = chatDepartment?.toLowerCase?.() || "";
      const selectedDeptNames = this.getSelectedDepartmentNames(selectedDepartments);

      return (
        selectedDepartments.includes(chatDepartment) || selectedDeptNames.includes(chatDeptLower)
      );
    },

    chatInSelectedDepartments(
      this: any,
      chat: any,
      selectedDeptIds: string[],
      departmentIndex: Record<string, any>
    ) {
      if (!selectedDeptIds?.length) return true;

      const chatId = chat.RowKey || chat.id;
      const chatDept = this.getChatDepartmentId(chat);

      if (!chatDept) {
        const rawDept = this.getRawDepartmentFromChat(chat);

        if (!rawDept) return false;

        const rawDeptLower = rawDept?.toLowerCase?.() || "";
        const selectedDeptNames = this.getSelectedDepartmentNames(selectedDeptIds);

        return selectedDeptIds.includes(rawDept) || selectedDeptNames.includes(rawDeptLower);
      }

      for (const deptId of selectedDeptIds) {
        const deptIndex = departmentIndex[deptId];

        if (deptIndex && deptIndex.queue?.has(chatId)) {
          return true;
        }
      }

      return selectedDeptIds.includes(chatDept);
    },

    getCountForDepartments(
      type: string,
      selectedDeptIds: string[],
      departmentIndex: Record<string, any>
    ) {
      if (!selectedDeptIds?.length) return 0;

      const allChatIds = new Set<string>();

      for (const deptId of selectedDeptIds) {
        const deptIndex = departmentIndex[deptId];
        if (deptIndex && deptIndex[type]) {
          deptIndex[type].forEach((id: string) => allChatIds.add(id));
        }
      }

      return allChatIds.size;
    },

    buildDepartmentIndex(this: any, chatsByType: Record<string, any[]>) {
      const index: Record<string, Record<string, Set<string>>> = {};

      Object.entries(chatsByType).forEach(([type, chats]) => {
        chats.forEach((chat: any) => {
          const chatId = chat.RowKey || chat.id;
          const department = this.getChatDepartmentId(chat);

          if (department) {
            if (!index[department]) {
              index[department] = {
                resolved: new Set(),
                abandoned: new Set(),
                inProgress: new Set(),
                queue: new Set(),
              };
            }
            index[department][type].add(chatId);
          }
        });
      });

      return index;
    },

    formatDuration(ms: number) {
      const duration = moment.duration(ms);
      const totalMinutes = Math.floor(duration.asMinutes());
      const seconds = duration.seconds();

      const mm = String(totalMinutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");

      return `${mm}:${ss}`;
    },

    formatDurationHuman(ms: number) {
      const duration = moment.duration(ms);
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },

    getChatId(chat: any) {
      return chat?.chatId || chat?.RowKey || chat?.id || null;
    },
  },
};
